import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ParkingArea, ParkingProviderOption, ParkingVehicleType, ParkingVehicleTypeDto, ParkingAreaDto } from '../../../../../core/models/system-settings/parking-master-data.model';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-parking-area',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-parking-area.component.html',
  styleUrl: './modal-parking-area.component.css',
})
export class ModalParkingAreaComponent implements OnInit {
  @Input() itemId = 0;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingArea;
  providers: ParkingProviderOption[] = [];
  vehicleTypes: Array<{ id: number; name: string }> = [];
  isLoading = signal(false);
  isSaving = signal(false);

  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean { return this.itemId > 0; }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyArea(this.siteId || null, this.siteName);
    const companyId = Number(this.cookieService.get('company_id') || 1);
    this.formData.companyId = companyId > 0 ? companyId : 1;
    this.loadDropdowns();
    if (this.isEdit) this.loadDetail();
  }

  onClose(): void { this.close.emit(); }

  imagePreview(base64: string): string {
    if (!base64) return '';
    if (base64.startsWith('data:')) return base64;
    return `data:image/jpeg;base64,${base64}`;
  }

  onPickImage(slot: 1 | 2 | 3 | 4, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toast.error('กรุณาเลือกไฟล์รูปภาพ');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const pure = result.includes(',') ? result.split(',')[1] : result;
      if (slot === 1) this.formData.image1Base64 = pure;
      if (slot === 2) this.formData.image2Base64 = pure;
      if (slot === 3) this.formData.image3Base64 = pure;
      if (slot === 4) this.formData.image4Base64 = pure;
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  clearImage(slot: 1 | 2 | 3 | 4): void {
    if (slot === 1) this.formData.image1Base64 = '';
    if (slot === 2) this.formData.image2Base64 = '';
    if (slot === 3) this.formData.image3Base64 = '';
    if (slot === 4) this.formData.image4Base64 = '';
  }

  onSave(): void {
    if (!this.formData.name?.trim()) { this.toast.error('กรุณาระบุชื่อ'); return; }
    if (!this.formData.siteId) { this.toast.error('กรุณาเลือกลาน'); return; }
    if (this.isEdit && !this.permissionService.canUpdate(this.router.url)) { this.toast.error('ไม่มีสิทธิ์แก้ไข'); return; }
    if (!this.isEdit && !this.permissionService.canCreate(this.router.url)) { this.toast.error('ไม่มีสิทธิ์สร้าง'); return; }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) { this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู'); return; }

    const payload = this.mapper.mapAreaPayload(this.formData, userId, menuId);
    const request$ = this.isEdit
      ? this.api.updateParkingArea(this.itemId, payload)
      : this.api.insertParkingArea(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadDropdowns(): void {
    this.api.getParkingProviders(this.siteId || undefined).subscribe({
      next: (res) => { this.providers = this.mapper.mapProviderOptions(res); },
      error: () => { this.providers = []; },
    });

    this.api.getParkingVehicleTypeForTable({ page: 1, pageSize: 200 }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable<ParkingVehicleTypeDto, ParkingVehicleType>(
          res,
          (item) => this.mapper.mapVehicleType(item)
        );
        this.vehicleTypes = mapped.data.map((x) => ({ id: x.id, name: x.name }));
      },
      error: () => { this.vehicleTypes = []; },
    });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getParkingAreaById(this.itemId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapArea(res as ParkingAreaDto);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายละเอียดไม่สำเร็จ');
        this.onClose();
      },
    });
  }

  private resolveUserId(): number {
    const fromStorage = Number(localStorage.getItem('userId') || 0);
    if (fromStorage > 0) return fromStorage;
    return Number(this.cookieService.get('user_id') || 0);
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
